import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { useGlobalStore } from '../../application/store/store';
import { NavigationRoutes } from '../../application/router/routes';

const MAX_TITLE_LENGTH = 200;
const MAX_CONTENT_LENGTH = 5000;

const CreateMessagePage = () => {
    const navigate = useNavigate();
    const {
        createMessage,
        messagesLoading,
        messagesError,
        clearMessagesError,
    } = useGlobalStore();

    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [titleError, setTitleError] = useState<string | null>(null);
    const [contentError, setContentError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const validateForm = (): boolean => {
        let isValid = true;

        if (!title.trim()) {
            setTitleError('Titel är obligatoriskt');
            isValid = false;
        } else if (title.length > MAX_TITLE_LENGTH) {
            setTitleError(`Titel får vara max ${MAX_TITLE_LENGTH} tecken`);
            isValid = false;
        } else {
            setTitleError(null);
        }

        if (!content.trim()) {
            setContentError('Meddelande är obligatoriskt');
            isValid = false;
        } else if (content.length > MAX_CONTENT_LENGTH) {
            setContentError(
                `Meddelande får vara max ${MAX_CONTENT_LENGTH} tecken`
            );
            isValid = false;
        } else {
            setContentError(null);
        }

        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            await createMessage({
                title: title.trim(),
                content: content.trim(),
            });
            setSuccessMessage('Meddelandet har skickats');
            setTimeout(() => {
                navigate(NavigationRoutes.messages);
            }, 1500);
        } catch (error) {
            console.error('Error creating message:', error);
        }
    };

    const handleCancel = () => {
        navigate(NavigationRoutes.messages);
    };

    return (
        <div className="p-3">
            <Box display="flex" alignItems="center" mb={3}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleCancel}
                    sx={{ mr: 2 }}
                >
                    Tillbaka
                </Button>
                <Typography variant="h4" fontWeight="bold">
                    Nytt meddelande
                </Typography>
            </Box>

            {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {successMessage}
                </Alert>
            )}

            {messagesError && (
                <Alert
                    severity="error"
                    onClose={clearMessagesError}
                    sx={{ mb: 2 }}
                >
                    {messagesError}
                </Alert>
            )}

            <Paper sx={{ p: 3 }}>
                <form onSubmit={handleSubmit}>
                    <TextField
                        label="Titel"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        required
                        error={!!titleError}
                        helperText={
                            titleError ||
                            `${title.length}/${MAX_TITLE_LENGTH} tecken`
                        }
                        sx={{ mb: 3 }}
                        disabled={messagesLoading || !!successMessage}
                        inputProps={{ maxLength: MAX_TITLE_LENGTH }}
                    />

                    <TextField
                        label="Meddelande"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        fullWidth
                        required
                        multiline
                        rows={10}
                        error={!!contentError}
                        helperText={
                            contentError ||
                            `${content.length}/${MAX_CONTENT_LENGTH} tecken`
                        }
                        sx={{ mb: 3 }}
                        disabled={messagesLoading || !!successMessage}
                        inputProps={{ maxLength: MAX_CONTENT_LENGTH }}
                    />

                    <Alert severity="info" sx={{ mb: 3 }}>
                        Meddelandet skickas till alla användare i organisationen
                    </Alert>

                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                            variant="outlined"
                            onClick={handleCancel}
                            disabled={messagesLoading || !!successMessage}
                        >
                            Avbryt
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={messagesLoading || !!successMessage}
                            startIcon={
                                messagesLoading ? (
                                    <CircularProgress
                                        size={16}
                                        color="inherit"
                                    />
                                ) : (
                                    <SendIcon />
                                )
                            }
                        >
                            {messagesLoading
                                ? 'Skickar...'
                                : 'Skicka meddelande'}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </div>
    );
};

export default CreateMessagePage;
